import type { ReactElement, ReactNode } from 'react';
import { useAuthContext } from '@citibox-panel/auth';
import { can, type Permission } from '@/lib/access';
import { useSession } from '@/lib/session';
import { signOutOfPanel } from '@/lib/signOut';
import { AccessStateScreen } from './AccessStateScreen';

export interface ViewHome {
  to: `/${string}`;
  permission: Permission;
}

export interface RequireViewProps {
  permission: Permission;
  home: ViewHome;
  children: ReactNode;
}

// UI gating only: until app-service answers 403 on these endpoints, a token without the permission
// can still call them.
export function RequireView({ permission, home, children }: RequireViewProps): ReactElement {
  const { permissions } = useSession();
  const { firebaseUser } = useAuthContext();
  if (can(permissions, permission)) return <>{children}</>;
  // Offered only when home would let them in; otherwise a home-less person bounces between denials.
  const homeAllowed = can(permissions, home.permission);
  return (
    <AccessStateScreen
      variant="denied"
      email={firebaseUser?.email ?? null}
      onSignOut={signOutOfPanel}
      {...(homeAllowed ? { homeHref: home.to } : {})}
    />
  );
}
